import { Link } from "react-router-dom";
import { ArrowUpRight, Camera, ImageIcon, Palette } from "lucide-react";
import { PRODUCT_LIST } from "@/lib/products";

const ICONS = { "royal-essence": Camera, sovereign: ImageIcon, "majestic-vibes": Palette };

export default function Products() {
  return (
    <section
      id="collection"
      data-testid="products-section"
      className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-32"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12 md:mb-16">
        <div className="max-w-2xl">
          <p className="overline">The Signature Experiences</p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.02]">
            Choose your <span className="italic text-[#D4AF37]">Crowned experience.</span>
          </h2>
        </div>
        <p className="text-white/60 max-w-md font-light leading-relaxed">
          Not products. Commissions. Three different creative paths — one
          finished physical piece: a 16&Prime; &times; 20&Prime; framed canvas,
          proofed with you before anything goes to production.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-5 md:gap-6">
        {PRODUCT_LIST.map((p) => (
          <ExperienceCard key={p.id} product={p} />
        ))}
      </div>
    </section>
  );
}

function ExperienceCard({ product: p }) {
  const Icon = ICONS[p.slug] || Camera;
  return (
    <Link
      to={`/collection/${p.slug}`}
      data-testid={`product-card-${p.id}`}
      className="group relative border border-white/10 hover:border-[#D4AF37]/60 transition-colors duration-500 overflow-hidden bg-[#111111] flex flex-col"
    >
      {/* Visual */}
      <div className="relative h-[240px] md:h-[260px] overflow-hidden">
        {p.image ? (
          <img
            src={p.image}
            alt={p.imageAlt}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-[radial-gradient(ellipse_70%_80%_at_50%_30%,rgba(130,73,55,0.5),transparent_70%)] flex items-center justify-center">
            <div className="border border-[#D4AF37]/40 w-24 h-32 flex items-center justify-center bg-black/40">
              <ImageIcon className="w-8 h-8 text-[#D4AF37]/70" strokeWidth={1} />
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-black/20 to-transparent" />
        <span className="absolute top-5 left-5 overline bg-[#824937] text-[#F7F1E7] px-3 py-1.5">
          {p.badge}
        </span>
      </div>

      {/* Body */}
      <div className="p-7 md:p-8 flex-1 flex flex-col">
        <div className="flex items-center gap-3">
          <Icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
          <p className="overline text-white/50">{p.shortName}</p>
        </div>
        <h3 className="font-serif-display text-xl md:text-2xl leading-snug mt-3">
          {p.headline}
        </h3>

        {/* Pricing clarity — total vs initial */}
        <dl className="mt-6 border-t border-white/10 pt-5 space-y-2 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-white/50">Finished piece</dt>
            <dd className="text-white/85 text-right" data-testid={`card-piece-${p.id}`}>{p.finishedPiece}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-white/50">Total experience</dt>
            <dd className="font-serif-display text-lg text-white">${p.pricing.total}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-white/50">Due today</dt>
            <dd className="font-serif-display text-lg text-[#D4AF37]">${p.pricing.initial}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-white/50">Remaining balance</dt>
            <dd className="text-white/80">${p.pricing.remaining}</dd>
          </div>
        </dl>

        <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between text-xs uppercase tracking-[0.22em] group-hover:text-[#D4AF37] transition-colors">
          <span>Explore The Experience</span>
          <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
        </div>
      </div>
    </Link>
  );
}
